import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AutoLinkContent from '@/components/seo/AutoLinkContent';
import { buildFAQSchema } from '@/components/seo/AutoSEOOptimizer';
import { ChevronDown } from 'lucide-react';

interface QAItem {
  q: string;
  a: string;
}

const getHeadingForPath = (path: string) => {
  if (path === '/') return 'Questions fréquentes';
  if (path.includes('/nos-services/location-bennes')) return 'FAQ · Location de bennes';
  if (path.includes('/nos-services/achat-metaux')) return 'FAQ · Achat de métaux';
  if (path.includes('/nos-services/demolition')) return 'FAQ · Démolition';
  if (path.includes('/valorisation/')) return 'FAQ · Valorisation';
  return 'FAQ';
};

const extractQAFromSchema = (schema: Record<string, any> | undefined): QAItem[] => {
  if (!schema) return [];
  const entities = Array.isArray(schema.mainEntity) ? schema.mainEntity : [];
  return entities
    .map((e) => ({
      q: e?.name ?? '',
      a: e?.acceptedAnswer?.text ?? ''
    }))
    .filter((x) => x.q && x.a);
};

const VisibleFAQ: React.FC = () => {
  const { pathname } = useLocation();

  const items = useMemo(() => {
    const schema = buildFAQSchema(pathname);
    return extractQAFromSchema(schema);
  }, [pathname]);

  if (!items.length) return null;

  return (
    <section aria-labelledby="faq-title" className="mx-auto max-w-5xl px-4 md:px-6 py-10">
      <AutoLinkContent>
        <article className="rounded-xl border bg-background text-foreground shadow-sm">
          <header className="px-6 pt-6">
            <h2 id="faq-title" className="text-2xl font-semibold tracking-tight">
              {getHeadingForPath(pathname)}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Trouve rapidement les réponses aux questions les plus courantes.</p>
          </header>

          <div className="divide-y">
            {items.map((item, idx) => (
              <details key={idx} open={idx === 0} className="group px-6 py-5 transition-colors hover:bg-muted/30 group-open:bg-muted/40">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                  <span className="text-base font-medium">{item.q}</span>
                  <ChevronDown className="mt-1 h-5 w-5 shrink-0 transition-transform group-open:rotate-180 text-muted-foreground" aria-hidden />
                </summary>
                <div className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
          <footer className="px-6 pb-6 flex flex-wrap gap-4">
            <a href="/nos-services/achat-metaux" className="inline-flex items-center gap-2 text-primary underline underline-offset-4 hover:no-underline" title="Vendre vos métaux – meilleurs prix">
              Vendre vos métaux · Meilleurs prix garantis
            </a>
            <a href="/nos-services/achat-metaux?region=ile-de-france" className="inline-flex items-center gap-2 text-primary/90 underline underline-offset-4 hover:no-underline" title="Achat de métaux en Île-de-France">
              Achat de métaux en Île‑de‑France
            </a>
            <a href="/nos-services/location-bennes?region=ile-de-france" className="inline-flex items-center gap-2 text-primary/90 underline underline-offset-4 hover:no-underline" title="Location de bennes en Île-de-France">
              Location de bennes en Île‑de‑France
            </a>
            <a href="/sites?region=ile-de-france" className="inline-flex items-center gap-2 text-primary/90 underline underline-offset-4 hover:no-underline" title="Nos sites en Île-de-France">
              Nos sites en Île‑de‑France
            </a>
          </footer>
        </article>
      </AutoLinkContent>
    </section>
  );
};

export default VisibleFAQ;
